import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
    Map,
    UtensilsCrossed,
    Receipt,
    ShoppingCart,
    CheckCheck,
    AlertCircle,
    RefreshCw,
    MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { useDiningSession } from "@/hooks/useDiningSession";
import { useCart } from "@/hooks/useCart";
import { currency, relativeMinutes } from "@/lib/format";
import LeaveOnTimeBanner from "@/components/LeaveOnTimeBanner";
import FloorTabContent from "@/components/dining/FloorTabContent";
import MenuExplorer from "@/components/dining/MenuExplorer";
import CartSheet from "@/components/dining/CartSheet";
import OrderTracker from "@/components/dining/OrderTracker";
import BillView from "@/components/dining/BillView";

export default function DiningSessionPage() {
    const { reservationId } = useParams();
    const navigate = useNavigate();

    const {
        reservation,
        menu,
        tables,
        orders,
        initialLoading,
        refreshing,
        lastUpdated,
        fetchReservation,
        fetchOrders,
    } = useDiningSession(reservationId);

    const cart = useCart();
    const [cartOpen, setCartOpen] = useState(false);
    const [tab, setTab] = useState("floor");
    const [checkingIn, setCheckingIn] = useState(false);

    const restaurant = reservation?.restaurant;
    const isActive = reservation && ["checked_in", "seated"].includes(reservation.status);
    const isBooked = reservation?.status === "booked";
    const isCompleted = reservation?.status === "completed";

    const categories = useMemo(() => groupMenuByCategory(menu), [menu]);

    const allItems = useMemo(() => orders.flatMap((o) => o.items), [orders]);
    const allServed = allItems.length > 0 && allItems.every((i) => i.status === "served");

    const checkIn = useCallback(
        async ({ manual = false }) => {
            setCheckingIn(true);
            try {
                const coords = manual ? null : await tryGetGeolocation();
                await api.post(`/reservations/${reservationId}/check-in`, {
                    lat: coords?.lat,
                    lng: coords?.lng,
                    manual: !!manual || coords === null,
                });
                toast.success("You're checked in!");
                await fetchReservation();
            } catch (error) {
                console.warn("[dining] check-in failed", error?.message);
                toast.error(error?.response?.data?.detail || "Check-in failed");
            } finally {
                setCheckingIn(false);
            }
        },
        [reservationId, fetchReservation]
    );

    const submitOrder = useCallback(async () => {
        if (!cart.cart.length) return;
        if (!isActive) {
            toast.error("Please check in before ordering");
            return;
        }
        try {
            await api.post("/orders", {
                reservation_id: reservationId,
                items: cart.cart.map((line) => ({
                    menu_item_id: line.menu_item_id,
                    quantity: line.quantity,
                    notes: line.notes || "",
                })),
            });
            toast.success("Order sent to the kitchen!");
            cart.clear();
            setCartOpen(false);
            setTab("order");
            await fetchOrders();
        } catch (error) {
            console.warn("[dining] place order failed", error?.message);
            toast.error(error?.response?.data?.detail || "Could not place order");
        }
    }, [cart, isActive, reservationId, fetchOrders]);

    const handleAddToCart = useCallback(
        (item) => {
            cart.addItem(item);
            toast.success(`${item.name} added to cart`, { duration: 1200 });
        },
        [cart]
    );

    if (initialLoading) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }
    if (!reservation) return null;

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
            <SessionHeader
                restaurant={restaurant}
                reservation={reservation}
                refreshing={refreshing}
                lastUpdated={lastUpdated}
            />

            {isBooked && (
                <LeaveOnTimeBanner
                    reservation={reservation}
                    restaurant={restaurant}
                    onCheckIn={(manual) => checkIn({ manual })}
                    checkingIn={checkingIn}
                />
            )}

            {isCompleted && (
                <Alert className="mb-4">
                    <CheckCheck className="h-4 w-4" />
                    <AlertTitle>Reservation complete</AlertTitle>
                    <AlertDescription>Thank you for dining with us! Hope to see you again soon.</AlertDescription>
                </Alert>
            )}

            <Tabs value={tab} onValueChange={setTab} data-testid="dining-tabs">
                <TabsList className="grid grid-cols-4 max-w-2xl">
                    <TabsTrigger value="floor" data-testid="tab-floor">
                        <Map className="h-3.5 w-3.5 mr-1" /> Floor
                    </TabsTrigger>
                    <TabsTrigger value="menu" data-testid="tab-menu" disabled={!isActive && !isCompleted}>
                        <UtensilsCrossed className="h-3.5 w-3.5 mr-1" /> Menu
                    </TabsTrigger>
                    <TabsTrigger value="order" data-testid="tab-order">
                        <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Order
                    </TabsTrigger>
                    <TabsTrigger value="bill" data-testid="tab-bill">
                        <Receipt className="h-3.5 w-3.5 mr-1" /> Bill
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="floor" className="mt-4">
                    <FloorTabContent
                        reservation={reservation}
                        tables={tables}
                        isBooked={isBooked}
                        isActive={isActive}
                        checkingIn={checkingIn}
                        onCheckIn={checkIn}
                    />
                </TabsContent>

                <TabsContent value="menu" className="mt-4">
                    {!isActive ? (
                        <Card>
                            <CardContent className="p-8 text-center">
                                <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                <p className="font-medium">Menu unlocks after check-in</p>
                                <p className="text-sm text-muted-foreground">
                                    Once you arrive and check in, you can browse the menu and order directly to the kitchen.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <MenuExplorer categories={categories} onAdd={handleAddToCart} />
                    )}
                </TabsContent>

                <TabsContent value="order" className="mt-4">
                    <OrderTracker orders={orders} onAddMore={() => setTab("menu")} canOrderMore={isActive} />
                </TabsContent>

                <TabsContent value="bill" className="mt-4">
                    <BillView
                        reservationId={reservationId}
                        reservation={reservation}
                        canRequestBill={isActive && allServed}
                        onPay={() => navigate(`/payment/${reservationId}`)}
                    />
                </TabsContent>
            </Tabs>

            {isActive && cart.cart.length > 0 && tab === "menu" && (
                <Button
                    onClick={() => setCartOpen(true)}
                    className="fixed bottom-6 right-6 h-14 px-5 rounded-full shadow-lg z-40"
                    data-testid="cart-open-button"
                >
                    <ShoppingCart className="h-5 w-5 mr-2" /> {cart.count} · {currency(cart.subtotal)}
                </Button>
            )}

            <CartSheet
                open={cartOpen}
                onOpenChange={setCartOpen}
                cart={cart.cart}
                subtotal={cart.subtotal}
                onChangeQuantity={cart.changeQuantity}
                onChangeNotes={cart.changeNotes}
                onRemove={cart.removeItem}
                onSubmit={submitOrder}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function groupMenuByCategory(menu) {
    const groups = {};
    for (const item of menu) {
        groups[item.category] = groups[item.category] || [];
        groups[item.category].push(item);
    }
    return groups;
}

async function tryGetGeolocation() {
    if (!("geolocation" in navigator)) return null;
    try {
        const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 });
        });
        return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch (error) {
        console.info("[dining] geolocation unavailable", error?.message);
        return null;
    }
}

function SessionHeader({ restaurant, reservation, refreshing, lastUpdated }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Dining at</div>
                <h1 className="font-display text-2xl sm:text-3xl">{restaurant?.name}</h1>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {restaurant?.address}
                </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className="capitalize" data-testid="reservation-status-strip">
                    {reservation.status.replace("_", " ")}
                </Badge>
                {reservation.table && (
                    <Badge variant="secondary" className="font-medium" data-testid="assigned-table-badge">
                        Table {reservation.table.label}
                    </Badge>
                )}
                <div
                    className="text-xs text-muted-foreground inline-flex items-center gap-1"
                    data-testid="order-last-updated-text"
                >
                    <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
                    {lastUpdated ? `Updated ${relativeMinutes(lastUpdated.toISOString())}` : ""}
                </div>
            </div>
        </div>
    );
}
